import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner"
;
import { SystemUser } from "@/pages/user/hooks/useUserManagement";

interface Department {
  id: string;
  name: string;
}

interface ChangeDepartmentProps {
  user: SystemUser
  onSave: (id: string, newDepartment: string, reason?: string) => void
  onCancel: () => void
}

export function ChangeDepartment({ user, onSave, onCancel }: ChangeDepartmentProps) {
  const [newDepartment, setNewDepartment] = useState<string>(user.department)
  const [reason, setReason] = useState("")
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      setIsLoadingDepartments(true);
      try {
        const { data, error } = await supabase
          .from('departments')
          .select('id, name')
          .order('name');
          
        if (error) throw error;
        setDepartments(data || []);
      } catch {
        toast.error('Failed to load departments');
      } finally {
        setIsLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);
  
  const handleSubmit = () => {
    if (newDepartment === user.department) {
      toast.info("Please select a different department.");
      return;
    }
    
    onSave(user.id, newDepartment, reason)
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Change Department</h2>
        <p className="text-muted-foreground">Move {user.name} to a different department</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Current Department</Label>
          <div className="p-2 border rounded-md bg-muted">
            {user.department || 'Unassigned'}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="new-department">New Department</Label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={newDepartment === ""}
                onChange={() => setNewDepartment("")}
                disabled={isLoadingDepartments}
              />
              <span>Unassigned</span>
            </label>
            {departments.map(dept => (
              <label key={dept.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={newDepartment === dept.name}
                  onChange={() => setNewDepartment(dept.name)}
                  disabled={isLoadingDepartments}
                />
                <span>{dept.name}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="reason">Reason for Change (Optional)</Label>
          <Textarea 
            id="reason"
            placeholder="Provide a reason for this department change"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          disabled={newDepartment === user.department}
        >
          Change Department
        </Button>
      </div>
    </div>
  )
} 